import { yupResolver } from "@hookform/resolvers/yup";
import { isEmpty } from "lodash";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useHistory, useLocation } from "react-router-dom";

import CircularProgress from "@mui/material/CircularProgress";
import { withStyles } from "@mui/styles";

import { Button, GridContainerWithSpacing as GridContainer, GridItemWithSpacing as GridItem } from "~/shared/components/MuiComponents.jsx";

import { googleValidateAddress } from "~/shared/addressValidation";
import {
  CompoundedStateNotSupportedModal,
  ConfirmAddressModal,
  CouldntVerifyAddressModal,
  MissingAddressComponentsModal,
  OrderCosts,
  POBoxShippingNotSupportedModal,
  SelectedSleepMeds,
  ShippingInputs,
  ShippingRadios,
  SleepOrderCosts,
  SleepSubscriptionSelectionSection,
  SleepConciergeInfo,
  SocialProof,
  StateNotSupportedModal,
  WhatHappensNext,
} from "./paymentComponents.jsx";
import {
  checkoutPostData,
  completeCheckoutAndRedirect,
  hasValidVisitAPIToken,
  orderDetailsFormValidationSchema,
  orderTotalPriceWithCouponDiscount,
  validatePromoCode,
} from "./paymentHelpers.js";

import { CheckoutProgress } from "../shared.jsx";

// Utils & Widgets
import {
  ORDER_TYPE_SLEEP_MEMBERSHIP_MAIL_ORDER_MONTHLY,
  ORDER_TYPE_SLEEP_MEMBERSHIP_VISIT_ONLY_MONTHLY,
  SHIPPING_METHOD_TWO_DAY,
} from "~/shared/sharedConstants";
import {
  getGoogleAPIKey,
  isCompoundedOrderType,
  isConsultationTypeInsomnia,
  isMailOrderType,
  isPharmacyPickupFromVisit,
  isPharmacyPickupOrderType,
  isSleepOrderType,
  supportedUSStates,
  supportedUSStatesForCompoundedMeds
} from "~/shared/utils/sharedUtils";
import { usStatesList } from "~/shared/widgets/autocompleteUSStates.jsx";
import {
  clearEditPendingVisitSession,
  clearPromoCode,
  getEditPendingVisitOriginalVisitID,
  getIsEditingPendingVisit,
  getLatestAddress,
  getOrderDetailsData,
  getOrderItemsList,
  getOrderItemSubcategory,
  getPromoCode,
  getShipmentMethod,
  getVisitDifferentPharmacy,
  getVisitDifferentPharmacyJSON,
  getVisitID,
  getVisitPhone,
  getVisitProduct,
  setPromoCode as savePromoCode,
  setConsultationTypeOrderItem,
  setOrderDetailsData,
  setOrderItemSubcategory,
  setShipmentMethod,
  setVisitProduct,
} from "~/visits/utils/visitCookies.js";
import KickClient from "~/visits/utils/api.js";
import usePendingEditTimeout from "~/visits/utils/usePendingEditTimeout.js";

import { kickAmaOrange, kickNavy } from "~/shared/styles/baseStyles.js";
import consultationStyles from "~/visits/styles/consultationStyles.js";

const latestAddress = getLatestAddress();

let initialFormStateValues = {
  address1: "",
  address2: "",
  city: "",
  zip: "",
  usState: "",
  phone: getVisitPhone(),
  smsConsent: true,
};

function getDefaultSleepOrderType(checkoutSessionID) {
  const currentProduct = getVisitProduct(checkoutSessionID);
  if (!isSleepOrderType(currentProduct)) {
    return isPharmacyPickupFromVisit(
      getVisitDifferentPharmacy(checkoutSessionID)
    )
      ? ORDER_TYPE_SLEEP_MEMBERSHIP_VISIT_ONLY_MONTHLY
      : ORDER_TYPE_SLEEP_MEMBERSHIP_MAIL_ORDER_MONTHLY;
  } else {
    return currentProduct;
  }
}

const OrderDetails = (props) => {
  const { classes } = props;
  const { consultationType } = props.match.params;
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const checkoutSessionID = queryParams.get("checkoutSessionID");
  if (!checkoutSessionID) {
    window.location.href = "/me/portal";
  } else if (!hasValidVisitAPIToken()) {
    // The order submission authenticates with the visit API token. A missing,
    // malformed, or expired token can't complete checkout (it 401s at submit),
    // so restart signup to reissue a fresh token rather than let the user fill
    // out the whole form and fail at the end.
    window.location.href = `/visits/${consultationType}/signup?error=token_expired`;
  }

  const history = useHistory();

  const isEditingPendingVisit = getIsEditingPendingVisit(checkoutSessionID);
  usePendingEditTimeout(checkoutSessionID);

  const googleAPIKey = getGoogleAPIKey();
  const savedOrderDetailsData = getOrderDetailsData(checkoutSessionID);

  // use saved order details if they exist (from going 'back' from payment details page)
  if (!isEmpty(savedOrderDetailsData)) {
    initialFormStateValues = {
      ...initialFormStateValues,
      ...savedOrderDetailsData,
      usState: usStatesList.find(
        (v) => v.abbreviation == savedOrderDetailsData.usState
      ),
    };
  } else if (!isEmpty(latestAddress)) {
    // otherwise try to use latest address (like in refills)
    initialFormStateValues = {
      ...initialFormStateValues,
      ...latestAddress,
      usState: usStatesList.find(
        (v) => v.abbreviation == latestAddress.usState
      ),
    };
  }

  // state variables
  const [sleepProductType, setSleepProductType] = useState(
    getDefaultSleepOrderType(checkoutSessionID)
  );
  const [showAddressModal, setShowAddressModal] = useState({});
  const [shipMethod, setShipMethod] = useState(
    getShipmentMethod(checkoutSessionID)
  );
  const [totalPrice, setTotalPrice] = useState(
    orderTotalPriceWithCouponDiscount(true, checkoutSessionID)
  );
  const [promoCode, setPromoCodeState] = useState(() =>
    getPromoCode(checkoutSessionID)
  );
  const [isValidatingPromoCode, setIsValidatingPromoCode] = useState(false);
  const [promoCodeError, setPromoCodeError] = useState("");
  const [
    { address1, address2, city, usState, zip, phone, smsConsent },
    setFormState,
  ] = useState(initialFormStateValues);
  const [formIsSubmitting, setFormIsSubmitting] = useState(false);

  useEffect(() => {
    // make sure correct cookie "product" is set for sleep visits
    if (isConsultationTypeInsomnia(consultationType)) {
      // Save subcategory before setVisitProduct clears order items
      const existingSubcategory = getOrderItemSubcategory(
        consultationType,
        checkoutSessionID,
      );
      setVisitProduct(sleepProductType, checkoutSessionID);
      setTotalPrice(orderTotalPriceWithCouponDiscount(true, checkoutSessionID));
      setConsultationTypeOrderItem(
        consultationType,
        sleepProductType,
        checkoutSessionID,
      );
      // Restore subcategory that was wiped by setVisitProduct -> clearOrderItems
      if (existingSubcategory) {
        setOrderItemSubcategory(
          consultationType,
          existingSubcategory,
          checkoutSessionID,
        );
      }
    }

    // set a default shipment method
    if (!getShipmentMethod(checkoutSessionID)) {
      setShipmentMethod("standard", checkoutSessionID);
    }

    // Re-validate stored promo code on page load
    const storedPromoCode = getPromoCode(checkoutSessionID);
    if (storedPromoCode?.code) {
      (async () => {
        setIsValidatingPromoCode(true);
        const result = await validatePromoCode(storedPromoCode.code);
        setIsValidatingPromoCode(false);

        if (!result.valid) {
          // Promo code is no longer valid - clear it
          setPromoCodeState(null);
          clearPromoCode(checkoutSessionID);
          setPromoCodeError(result.message || "Promo code is no longer valid");
          setTotalPrice(
            orderTotalPriceWithCouponDiscount(true, checkoutSessionID, null)
          );
        } else {
          // Promo code is still valid - update total price with discount
          setTotalPrice(
            orderTotalPriceWithCouponDiscount(
              true,
              checkoutSessionID,
              storedPromoCode
            )
          );
        }
      })();
    }

    if (typeof window.Beacon == "function") {
      // window.Beacon("init", "e67f20b1-8927-4c18-b2a9-3e8119a361ad");
    }
  }, []);

  const setSleepVisitProductFromRadio = (event) => {
    const productType = event.target.value;
    setVisitProduct(productType, checkoutSessionID);
    setSleepProductType(productType);
    setTotalPrice(orderTotalPriceWithCouponDiscount(true, checkoutSessionID));
    console.log(
      "differentPharmacyJSON: ",
      getVisitDifferentPharmacyJSON(checkoutSessionID)
    );
    console.log("getVisitProduct: ", getVisitProduct(checkoutSessionID));

    setConsultationTypeOrderItem(
      consultationType,
      productType,
      checkoutSessionID
    );
  };

  const isPOBox = (address) => {
    const poBoxRegex = /(P\.?O\.?\s*Box|Post\s*Office\s*Box|PO\s*Box)/gi;
    return (
      poBoxRegex.test(address.address1) || poBoxRegex.test(address.address2)
    );
  };

  const handleApplyPromoCode = async (code) => {
    if (!code) {
      setPromoCodeState(null);
      clearPromoCode(checkoutSessionID);
      setPromoCodeError("");
      setTotalPrice(
        orderTotalPriceWithCouponDiscount(true, checkoutSessionID, null)
      );
      return;
    }

    setIsValidatingPromoCode(true);
    setPromoCodeError("");

    const result = await validatePromoCode(code);
    setIsValidatingPromoCode(false);

    if (result.valid) {
      const newPromoState = {
        code: code,
        promotionCodeID: result.promotionCodeID,
        couponID: result.couponID,
        couponName: result.couponName,
        percentOff: result.percentOff || 0,
        amountOff: result.amountOff || 0,
        isValid: true,
      };

      setPromoCodeState(newPromoState);
      savePromoCode(newPromoState, checkoutSessionID);

      // Clear Rewardful coupon (promo code takes precedence)
      if (window.Rewardful?.coupon) {
        delete window.Rewardful.coupon;
      }

      setTotalPrice(
        orderTotalPriceWithCouponDiscount(
          true,
          checkoutSessionID,
          newPromoState
        )
      );
    } else {
      setPromoCodeError(result.message || "Invalid promo code");
    }
  };

  const validateAddress = async (apiKey, address) => {
    // Check for P.O. box with two-day shipping before Google validation
    if (isPOBox(address) && shipMethod === SHIPPING_METHOD_TWO_DAY) {
      setFormIsSubmitting(false);
      setShowAddressModal({
        modalName: "poBoxShippingNotSupported",
      });
      return;
    }

    const fixAddressModal = await googleValidateAddress(apiKey, address);
    setFormIsSubmitting(false);
    if (fixAddressModal) {
      const { modalName, modalData } = fixAddressModal;
      setShowAddressModal({ modalName, modalData });
      setFormState(address);
    } else {
      finishOrderDetailsSection(address);
    }
  };

  const [checkoutError, setCheckoutError] = useState("");

  const finishOrderDetailsSection = async (orderDetails) => {
    setShowAddressModal({}); // clear address modal
    setFormState(orderDetails);
    setFormIsSubmitting(false);

    // double check that google address validation didn't set an unsupported state
    if (
      isMailOrderType(getVisitProduct(checkoutSessionID)) &&
      !supportedUSStates.includes(orderDetails.usState.abbreviation)
    ) {
      setShowAddressModal({
        modalName: "stateNotSupported",
        modalData: {
          unsupportedState: orderDetails.usState.name,
        },
      });
      return;
    }

    setOrderDetailsData(
      {
        ...orderDetails,
        usState: orderDetails.usState.abbreviation,
      },
      checkoutSessionID
    );

    // Edit Pending Visit flow: don't hit Stripe — PATCH the existing pending visit and bounce
    // back to /me/portal. The backend handler atomically replaces order_items + pharmacy +
    // order_type and clears the system patient lock.
    if (isEditingPendingVisit) {
      const visitID =
        getEditPendingVisitOriginalVisitID(checkoutSessionID) ||
        getVisitID(checkoutSessionID);
      const orderItemsRaw = getOrderItemsList(checkoutSessionID);
      // app.OrderItem unmarshals camelCase JSON (productSlug, isAddon) on the Go side.
      const orderItems = orderItemsRaw.map((oi) => ({
        consultationType: oi.consultationType,
        productSlug: oi.productSlug,
        subcategory: oi.subcategory || "",
        isAddon: !!oi.isAddon,
        quantity: oi.quantity || 1,
      }));
      const pharmacyInfoRaw =
        getVisitDifferentPharmacyJSON(checkoutSessionID) || {};
      const payload = {
        order_type: getVisitProduct(checkoutSessionID) || "",
        shipment_method: getShipmentMethod(checkoutSessionID) || "",
        pharmacy_info: pharmacyInfoRaw,
        order_items: orderItems,
        // Persist the (Google-validated) address from this form so shipping/billing edits stick.
        address1: orderDetails.address1 || "",
        address2: orderDetails.address2 || "",
        city: orderDetails.city || "",
        us_state: orderDetails.usState?.abbreviation || orderDetails.usState || "",
        zip: orderDetails.zip || "",
      };
      try {
        await KickClient.newAuthenticatedClient().patch(
          `/visits/${visitID}/edit-pending`,
          payload
        );
        clearEditPendingVisitSession(checkoutSessionID);
        window.location.href = "/me/portal";
      } catch (err) {
        const errMsg =
          err?.response?.data?.error ||
          "We couldn't update your visit. Please try again or contact support.";
        setCheckoutError(errMsg);
        setFormIsSubmitting(false);
      }
      return;
    }

    // If the total price is $0 (100% discount), skip payment details and complete checkout directly
    const currentPromoCode = getPromoCode(checkoutSessionID);
    const currentTotalPrice = orderTotalPriceWithCouponDiscount(
      true,
      checkoutSessionID,
      currentPromoCode
    );

    if (currentTotalPrice <= 0) {
      setFormIsSubmitting(true);
      const postData = checkoutPostData(
        { cardName: "" },
        consultationType,
        checkoutSessionID,
        currentPromoCode
      );

      await completeCheckoutAndRedirect({
        paymentMethodID: "",
        postData,
        checkoutSessionID,
        consultationType,
        history,
        setError: setCheckoutError,
      });
      setFormIsSubmitting(false);
      return;
    }

    history.push(
      `/visits/${consultationType}/payment/payment-details?checkoutSessionID=${checkoutSessionID}`
    );
  };

  // form props
  const {
    control,
    handleSubmit,
    getValues,
    trigger,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(orderDetailsFormValidationSchema),
  });

  return (
    <div className={`${classes.containerWithSpacing} payment-section`}>
      {!isEditingPendingVisit && (
        <div className={classes.checkoutProgressMobile}>
          <CheckoutProgress step={1} checkoutSessionID={checkoutSessionID} />
        </div>
      )}
      <GridContainer>
        <GridItem xs={12} md={4}>
          <SocialProof
            style={{ borderBottom: "1px solid #ddd", marginBottom: "24px" }}
            showtestimonials={isConsultationTypeInsomnia(
              consultationType
            ).toString()}
          />
          <ShippingRadios
            classes={classes}
            shipMethod={shipMethod}
            setShipMethod={setShipMethod}
            checkoutSessionID={checkoutSessionID}
            setTotalPrice={setTotalPrice}
          />
          {isConsultationTypeInsomnia(consultationType) && (
            <SleepSubscriptionSelectionSection
              consultationType={consultationType}
              sleepProductType={sleepProductType}
              setSleepVisitProductFromRadio={setSleepVisitProductFromRadio}
              classes={classes}
              checkoutSessionID={checkoutSessionID}
            />
          )}
          {isConsultationTypeInsomnia(consultationType) && (
            <SelectedSleepMeds />
          )}
          {isConsultationTypeInsomnia(consultationType) && <WhatHappensNext />}
          {isConsultationTypeInsomnia(consultationType) && (
            <SleepConciergeInfo />
          )}
        </GridItem>
        <GridItem xs={12} md={8}>
          <div className={classes.paymentForm}>
            {!isEditingPendingVisit && (
              <div className={classes.checkoutProgressDesktop}>
                <CheckoutProgress
                  step={1}
                  checkoutSessionID={checkoutSessionID}
                />
              </div>
            )}
            <form
              style={{ width: "100%" }}
              onSubmit={handleSubmit(async (data) => {
                console.log("[OrderDetails onSubmit] errors: ", errors);
                console.log(data);
              })}
            >
              <p className={classes.subtitle}>
                {isPharmacyPickupOrderType(getVisitProduct(checkoutSessionID))
                  ? "Billing Address"
                  : "Shipping Address"}
              </p>
              {!isPharmacyPickupOrderType(
                getVisitProduct(checkoutSessionID)
              ) && (
                <p
                  style={{
                    fontSize: "14px",
                    color: "#666",
                    marginTop: "-2px",
                    marginBottom: "16px",
                  }}
                >
                  Your home, office, or somewhere you collect packages
                </p>
              )}

              <StateNotSupportedModal
                showModal={showAddressModal.modalName === "stateNotSupported"}
                setShowModal={setShowAddressModal}
                modalData={showAddressModal.modalData}
                consultationType={consultationType}
                checkoutSessionID={checkoutSessionID}
              />

              <CompoundedStateNotSupportedModal
                showModal={
                  showAddressModal.modalName === "compoundedStateNotSupported"
                }
                setShowModal={setShowAddressModal}
                modalData={showAddressModal.modalData}
                consultationType={consultationType}
              />

              <ConfirmAddressModal
                showModal={showAddressModal.modalName === "confirmAddress"}
                setShowModal={setShowAddressModal}
                modalData={showAddressModal.modalData}
                submit={(data) => {
                  if (data) {
                    validateAddress(googleAPIKey, {
                      ...getValues(),
                      ...data,
                    });
                  } else {
                    finishOrderDetailsSection(getValues());
                  }
                }}
              />

              <MissingAddressComponentsModal
                showModal={
                  showAddressModal.modalName === "missingAddressComponents"
                }
                setShowModal={setShowAddressModal}
                modalData={showAddressModal.modalData}
                submit={(data) => {
                  if (data) {
                    validateAddress(googleAPIKey, {
                      ...getValues(),
                      ...data,
                    });
                  } else {
                    finishOrderDetailsSection(getValues());
                  }
                }}
              />

              <CouldntVerifyAddressModal
                showModal={
                  showAddressModal.modalName === "couldntVerifyAddress"
                }
                setShowModal={setShowAddressModal}
                modalData={showAddressModal.modalData}
                submit={() => {
                  finishOrderDetailsSection(getValues());
                }}
              />

              <POBoxShippingNotSupportedModal
                showModal={
                  showAddressModal.modalName === "poBoxShippingNotSupported"
                }
                setShowModal={setShowAddressModal}
                message={showAddressModal.modalData?.message}
              />

              <ShippingInputs
                classes={classes}
                errors={errors}
                control={control}
                address1={address1}
                address2={address2}
                city={city}
                usState={usState}
                zip={zip}
                phone={phone}
                smsConsent={smsConsent}
              />

              {isConsultationTypeInsomnia(consultationType) ? (
                <SleepOrderCosts
                  classes={classes}
                  consultationType={consultationType}
                  shipMethod={shipMethod}
                  totalPrice={totalPrice}
                  checkoutSessionID={checkoutSessionID}
                  promoCode={promoCode}
                  onApplyPromoCode={handleApplyPromoCode}
                  isValidatingPromoCode={isValidatingPromoCode}
                  promoCodeError={promoCodeError}
                />
              ) : (
                <OrderCosts
                  classes={classes}
                  consultationType={consultationType}
                  shipMethod={shipMethod}
                  totalPrice={totalPrice}
                  checkoutSessionID={checkoutSessionID}
                  promoCode={promoCode}
                  onApplyPromoCode={handleApplyPromoCode}
                  isValidatingPromoCode={isValidatingPromoCode}
                  promoCodeError={promoCodeError}
                />
              )}

              {checkoutError && (
                <p
                  style={{
                    color: "red",
                    marginTop: "15px",
                    marginBottom: "10px",
                    fontSize: "17px",
                  }}
                >
                  {checkoutError}
                </p>
              )}

              {isEditingPendingVisit && (
                <p
                  style={{
                    color: kickNavy,
                    marginTop: "16px",
                    marginBottom: "10px",
                    fontSize: "15px",
                    lineHeight: 1.4,
                  }}
                >
                  You haven&apos;t been charged for this visit yet — and you
                  won&apos;t be until a doctor reviews it and writes your
                  prescriptions.
                </p>
              )}

              <Button
                disabled={formIsSubmitting}
                onClick={async (e) => {
                  setFormIsSubmitting(true);
                  e.preventDefault();
                  const valid = await trigger([
                    "address1",
                    "city",
                    "usState",
                    "zip",
                    "phone",
                  ]);
                  if (valid) {
                    const formValues = getValues();
                    const stateAbbr = formValues.usState?.abbreviation;
                    // check if state is supported
                    if (
                      isMailOrderType(getVisitProduct(checkoutSessionID)) &&
                      !supportedUSStates.includes(stateAbbr)
                    ) {
                      setFormIsSubmitting(false);
                      setShowAddressModal({
                        modalName: "stateNotSupported",
                        modalData: {
                          unsupportedState: formValues.usState.name,
                        },
                      });
                    } else {
                      const orderItems = getOrderItemsList(checkoutSessionID);
                      const hasFlavoredOrderItems = Object.values(
                        orderItems
                      ).some((orderItem) =>
                        isCompoundedOrderType(orderItem.productSlug)
                      );
                      if (
                        hasFlavoredOrderItems &&
                        !supportedUSStatesForCompoundedMeds.includes(stateAbbr)
                      ) {
                        setFormIsSubmitting(false);
                        setShowAddressModal({
                          modalName: "compoundedStateNotSupported",
                          modalData: {
                            unsupportedState: formValues.usState.name,
                          },
                        });
                        return;
                      }
                      await validateAddress(googleAPIKey, formValues);
                    }
                  } else {
                    setFormIsSubmitting(false);
                  }
                }}
                className={classes.confirmAndPayButton}
                style={{
                  fontSize: "18px",
                  fontWeight: "bold",
                  textTransform: "unset",
                  backgroundColor: kickAmaOrange,
                  color: kickNavy,
                }}
              >
                {isEditingPendingVisit ? "Update Visit" : "Next"}
                {formIsSubmitting ? (
                  <CircularProgress
                    size="large"
                    style={{
                      position: "relative",
                      left: "24px",
                      top: "1px",
                      color: kickNavy,
                    }}
                  />
                ) : null}
              </Button>

              {isEditingPendingVisit && (
                <Button
                  onClick={() => {
                    // Discard the in-progress edit and return to the portal. Clears this edit
                    // session's cookies (and the global fulfillment flags) so nothing leaks into
                    // the next edit; the portal landing also resets as a backstop.
                    clearEditPendingVisitSession(checkoutSessionID);
                    window.location.href = "/me/portal";
                  }}
                  style={{
                    display: "block",
                    margin: "12px auto 0",
                    textTransform: "unset",
                    color: kickNavy,
                    fontSize: "15px",
                  }}
                >
                  Cancel changes
                </Button>
              )}
            </form>
          </div>
        </GridItem>
      </GridContainer>
    </div>
  );
};

export default withStyles(consultationStyles)(OrderDetails);
